import {
  addDays,
  differenceInMinutes,
  format,
  isBefore,
  isPast,
  isToday,
  isYesterday,
  startOfDay,
} from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  IRestaurantTimeSlot,
  useGetRestaurantQuery,
} from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { useCartContext } from '@fhs-legacy/frontend/src/state/cart';
import { OrderStatus } from '@fhs-legacy/frontend/src/state/order';
import { useServiceModeContext } from '@fhs-legacy/frontend/src/state/service-mode';
import { useStoreContext } from '@fhs-legacy/frontend/src/state/store';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';

import { IDatePickerData, ISelectedDate, ITimePickerData } from './types';
import { useRestaurantOpenStatus } from './use-restaurant-open-status';

interface UseDatePickerOptionsResult {
  datePickerData: IDatePickerData[] | undefined;
  firstAvailableDate: Date | undefined;
  firstOptionFormatted: ISelectedDate | undefined;
  isLoadingData: boolean;
  hasError: boolean;
  isRestaurantOpen: boolean;
}

const MINUTE_IN_MS = 60000;

const FUTURE_ORDERING_OPTIONS = {
  timeInterval: 15,
  days: 7,
  minutesBeforeStoreCloses: 15,
};

const TIME_FIRED_OPTIONS = {
  timeInterval: 15,
  days: 1,
  length: 5,
  minutesBeforeStoreCloses: 15,
};

export function useDatePickerOptions(): UseDatePickerOptionsResult {
  const enableFutureOrdering = useFlag('enable-future-ordering');
  const enableTodayAvailableOptionsFromBackend = useFlag(
    'enable-today-available-pickup-options-from-backend'
  );

  const { serverOrder, orderStatus } = useCartContext();
  const { store } = useStoreContext();
  const { serviceMode } = useServiceModeContext();
  const { isRestaurantOpen } = useRestaurantOpenStatus();

  const earliestPickupTimeInSeconds =
    (serverOrder as { earliestPickupTimeInSeconds?: number })?.earliestPickupTimeInSeconds ?? 0;

  const { data, loading, error } = useGetRestaurantQuery({
    variables: { storeNumber: store.number },
    skip: !store,
    fetchPolicy: 'cache-first',
  });

  const [datePickerData, setDatePickerData] = useState<IDatePickerData[]>();
  const [firstOptionFormatted, setFirstOptionFormatted] = useState<ISelectedDate>();

  const firstAvailableDate = useMemo(
    () =>
      firstOptionFormatted
        ? new Date(
            firstOptionFormatted.year,
            firstOptionFormatted.month,
            firstOptionFormatted.day,
            firstOptionFormatted.hour,
            firstOptionFormatted.minutes
          )
        : undefined,
    [firstOptionFormatted]
  );

  const getDatePickerData = useCallback(() => {
    if (!data?.restaurant?.futureServiceHours?.length) {
      return undefined;
    }

    const storeData = data.restaurant.futureServiceHours.find(
      slot => slot.serviceMode === serviceMode
    );

    if (!storeData?.timeSlots?.length) {
      return undefined;
    }

    return buildDatePickerData(
      storeData.timeSlots as IRestaurantTimeSlot[],
      enableFutureOrdering,
      enableTodayAvailableOptionsFromBackend,
      isRestaurantOpen,
      earliestPickupTimeInSeconds,
      (serverOrder as { availableTimeOptions?: string[] })?.availableTimeOptions || []
    );
  }, [
    data,
    enableTodayAvailableOptionsFromBackend,
    serviceMode,
    enableFutureOrdering,
    isRestaurantOpen,
    earliestPickupTimeInSeconds,
    serverOrder,
  ]);

  useEffect(() => {
    if (data?.restaurant && orderStatus === OrderStatus.PRICE_SUCCESSFUL) {
      const options = getDatePickerData();
      setDatePickerData(options);

      const firstAvailableDay = options?.find(
        item => !item.isUnavailable && item.options.length > 0 && !isPast(item.options[0]?.time)
      );
      if (firstAvailableDay?.options[0]) {
        const firstTime = firstAvailableDay.options[0].time;
        setFirstOptionFormatted({
          year: firstTime.getFullYear(),
          month: firstTime.getMonth(),
          day: firstTime.getDate(),
          hour: firstTime.getHours(),
          minutes: firstTime.getMinutes(),
        });
      }
    }
  }, [getDatePickerData, data, orderStatus]);

  const isLoadingData =
    loading ||
    !orderStatus ||
    ![
      OrderStatus.PRICE_SUCCESSFUL,
      OrderStatus.INSERT_SUCCESSFUL,
      OrderStatus.PRICE_ERROR,
    ].includes(orderStatus);

  const hasError = !!error || orderStatus === OrderStatus.PRICE_ERROR;

  return {
    datePickerData,
    firstAvailableDate,
    firstOptionFormatted,
    isLoadingData,
    hasError,
    isRestaurantOpen,
  };
}

/**
 * Build complete date picker data from raw time slots.
 * Handles sanitization, time slot generation, and validation.
 */
function buildDatePickerData(
  rawTimeSlots: IRestaurantTimeSlot[],
  enableFutureOrdering: unknown,
  enableTodayAvailableOptionsFromBackend: unknown,
  isRestaurantOpen: boolean,
  earliestPickupTimeInSeconds: number,
  todayAvailableTimeOptions: string[]
): IDatePickerData[] {
  const options = enableFutureOrdering ? FUTURE_ORDERING_OPTIONS : TIME_FIRED_OPTIONS;
  const earliestPickupMinutes = earliestPickupTimeInSeconds / 60;

  // Helper function to find or create a day slot
  const findDayInTimeSlots = (slots: IRestaurantTimeSlot[], day: string): IRestaurantTimeSlot => {
    const dayToFind = slots.find(slot => slot?.date === day);
    if (dayToFind) {
      return dayToFind;
    }
    // Generate missing date and presume the store is closed that day
    const openAndCloseHours = new Date(day + 'T00:00:00');
    openAndCloseHours.setHours(0);
    openAndCloseHours.setMinutes(0);
    openAndCloseHours.setSeconds(0);
    const formattedOpenAndCloseHours = format(openAndCloseHours, 'yyyy-MM-dd HH:mm:ss');
    const formattedDate = format(openAndCloseHours, 'yyyy-MM-dd');
    return {
      date: formattedDate,
      isClosed: true,
      opens: formattedOpenAndCloseHours,
      closes: formattedOpenAndCloseHours,
    };
  };

  // Generate time slots for consecutive days starting from today
  // For catering, skip the first day (cateringDaysAhead)
  const sanitizedTimeSlots: IRestaurantTimeSlot[] = [];

  for (let i = 0; i < options.days; i++) {
    const day = format(addDays(startOfDay(new Date()), i), 'yyyy-MM-dd');
    const timeSlot = findDayInTimeSlots(rawTimeSlots, day);
    sanitizedTimeSlots.push(timeSlot);
  }

  // Add yesterday as an option to show past midnight available options for an open store.
  // e.g. store closes 2 am. and it is currently 1 am.
  const yesterdaySlot = rawTimeSlots.find(slot => isYesterday(new Date(slot.date)));
  if (
    yesterdaySlot &&
    sanitizedTimeSlots.length > 0 &&
    isToday(new Date(sanitizedTimeSlots[0].date)) &&
    isBefore(new Date(yesterdaySlot.closes), new Date(yesterdaySlot.opens))
  ) {
    sanitizedTimeSlots.unshift({
      date: yesterdaySlot.date,
      closes: format(addDays(new Date(yesterdaySlot.closes), 1), 'yyyy-MM-dd HH:mm:ss'),
      opens: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      isClosed: false,
    });
  }

  // Build date picker data for each day
  const result = sanitizedTimeSlots.map((timeSlot, index) => {
    const { opens = '', closes = '', date = '', isClosed } = timeSlot;
    const isFirstDay = index === 0;
    const dateTimeWithTime = date + 'T12:00:00';

    const timeSlotOpens = parseLocalDate(opens);
    let timeSlotCloses = parseLocalDate(closes);

    // Update timeSlotCloses if the store closes past midnight (e.g. 2 am)
    const isYesterdaySlot = isYesterday(new Date(dateTimeWithTime));
    if (!isYesterdaySlot) {
      const storeClosesPastMidnight = isBefore(timeSlotCloses, timeSlotOpens);
      timeSlotCloses = storeClosesPastMidnight ? addDays(timeSlotCloses, 1) : timeSlotCloses;
    }

    // Generate time slots from now for today (if store is open) and from the open date for the rest of the week
    const startingDate = getStartingDate(isRestaurantOpen, timeSlotOpens, isYesterdaySlot);

    // We need to disable the first day in the array in case the store is already closed for the day
    const isFirstDayNotAvailable = getIsFirstDayNotAvailable(timeSlotCloses);

    const lastPossibleSlot =
      timeSlotCloses.getTime() - options.minutesBeforeStoreCloses * MINUTE_IN_MS;

    const offset = enableFutureOrdering ? earliestPickupMinutes : 0;
    const length = enableFutureOrdering
      ? differenceInMinutes(timeSlotCloses, startingDate) / options.timeInterval
      : TIME_FIRED_OPTIONS.length;

    let dateOptions: ITimePickerData[] = [];

    // Get available time options from BE for today if flag is enabled
    if (
      enableTodayAvailableOptionsFromBackend &&
      isFirstDay &&
      todayAvailableTimeOptions.length > 0
    ) {
      const adjustedTime = new Date(startingDate.getTime() + offset * MINUTE_IN_MS);
      dateOptions = todayAvailableTimeOptions
        .map(timeOption => ({ time: parseLocalDate(timeOption) }))
        .filter(option => option.time >= adjustedTime && option.time.getTime() < lastPossibleSlot);
    } else {
      // Generate time slots based on interval
      const totalSlots = Math.floor(length);
      for (let i = 0; i < totalSlots; i++) {
        const slotTime = getDateRoundUpToNearestFiveMinutes(
          new Date(startingDate.getTime() + (i * options.timeInterval + offset) * MINUTE_IN_MS)
        );
        if (slotTime.getTime() < lastPossibleSlot) {
          dateOptions.push({ time: slotTime });
        }
      }
    }

    // Check if this day should be disabled
    const isUnavailable = !dateOptions.length || isClosed || isFirstDayNotAvailable;

    return { date, isUnavailable, options: dateOptions };
  });

  return result;
}

function parseLocalDate(dateString: string): Date {
  // If it already has a 'T' separator, use it as-is
  if (dateString.includes('T')) {
    return new Date(dateString);
  }
  // Replace space with 'T' to ensure it's parsed as local time
  return new Date(dateString.replace(' ', 'T'));
}

function getDateRoundUpToNearestFiveMinutes(date = new Date()): Date {
  const ms = 1000 * 60 * 5; // convert 5 minutes to ms
  const roundedDate = new Date(Math.ceil(date.getTime() / ms) * ms);
  return roundedDate;
}

function getStartingDate(
  isRestaurantOpen: boolean,
  timeSlotOpens: Date,
  isYesterdaySlot: boolean
): Date {
  // Always start from now if the current slot is yesterday since this is for past midnight options
  if (isYesterdaySlot) {
    return new Date();
  }
  return isToday(timeSlotOpens) && isRestaurantOpen && isBefore(timeSlotOpens, new Date())
    ? new Date() // Start options from now - store is already open
    : timeSlotOpens;
}

function getIsFirstDayNotAvailable(timeSlotCloses: Date): boolean {
  return isToday(timeSlotCloses) && new Date().getTime() > timeSlotCloses.getTime();
}
